/* eslint-disable dot-notation */
// disable dot-notation to be able to use bracket syntax on global object
import { FC, useEffect } from 'react';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import { FragmentConfiguration } from '@/config';
import { Product } from '@/interfaces';
import { GtmEventPageNavigation, GtmEventProductView } from './types';
import useOutcome from '../hooks/useOutcome';
import { useCategory, useProduct } from '../ProductContext';

type TrackingProps = {
  pagegroup?: string;
  product?: Product;
  position?: number;
};

export const Tracking: FC<TrackingProps> = ({ pagegroup = 'KS_Detailview', position = 1 }) => {
  // Soasta Tracking Variables
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/dot-notation
    global['version'] = 'emma';
    // eslint-disable-next-line @typescript-eslint/dot-notation
    global['emma'] = true;
    // eslint-disable-next-line @typescript-eslint/dot-notation
    global['emma_pagegroup'] = pagegroup;
  }, [pagegroup]);

  useOutcome();

  const dispatchGTMEvent = useTracking();
  const { category, query } = useCategory();
  const { product } = useProduct();

  const {
    tracking: {
      values: { siteName },
    },
  } = useConfig<FragmentConfiguration>();

  let akl: string;
  let selectedDimensionValues: string[][];
  let variant: string;
  if (product) {
    akl = product.akl;
    selectedDimensionValues = product.dimensions.map((dimension) =>
      dimension.values
        .filter((dimObj) => dimObj.isSelected === true)
        .map((dimVal) => dimVal.displayText),
    );
    variant = [].concat(...selectedDimensionValues).join('|');
  }

  /* EECproductDetailView; do before pageNavigation for data availability */
  useEffect(() => {
    // use name prop to determine if product data is available
    if (typeof window !== 'undefined' && product) {
      const {
        sku,
        availability,
        brand,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        marketcode,
        name,
        orderNumber,
        price,
        slug,
        promotion,
      } = product;

      let determinedAvailability: '0' | '1' | '2';
      switch (availability.state) {
        case 'AVAILABLE':
          determinedAvailability = '1';
          break;
        case 'NOT_YET_AVAILABLE':
        case 'NOT_AVAILABLE':
          determinedAvailability = '0';
          break;
        case 'UNSELECTED':
        default:
          determinedAvailability = '2';
      }

      dispatchGTMEvent<GtmEventProductView>({
        Seitenpfad: `/p/${slug}/${akl}`,
        Seitentyp: 'detailview',
        ShopID: category,
        UNITO_Bewertung: 'nicht genutzt',
        bundle: product.sku.concat(siteName),
        ecommerce: {
          detail: {
            products: [
              {
                availability: determinedAvailability,
                brand: brand ? brand.name : '',
                category: query ? `search:${query}` : category,
                dimension1: availability.message,
                dimension2: determinedAvailability,
                dimension3: null, // should be rating
                dimension4: (price.valueOld ? price.valueOld / 100 : price.value / 100).toFixed(2),
                dimension5: sku,
                dimension6: akl,
                dimension8: promotion,
                id: orderNumber,
                marketcode: marketcode?.toString(),
                name,
                oldPrice: (price.valueOld ? price.valueOld / 100 : price.value / 100).toFixed(2),
                position,
                price: (price.value / 100).toFixed(2),
                variant,
              },
            ],
          },
        },
        event: 'EECproductDetailView',
      });
    }
  }, [akl, variant]);

  /* PDVPageNavigation event: new product loaded */
  useEffect(() => {
    if (typeof window !== 'undefined' && akl) {
      dispatchGTMEvent<GtmEventPageNavigation>({
        event: 'ga_event',
        eventAction: 'NavigationClick',
        eventCategory: 'pageNavigation',
        eventLabel: 'DetailviewNavigation',
      });
    }
  }, [akl]);

  return null;
};

export default Tracking;
